import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginScreen from './components/LoginScreen';
import RestaurantManagement from './components/RestaurantManagement';
import CategoriesManagement from './components/CategoriesManagement';
import CustomerMenuInterface from './components/CustomerMenuInterface'; 
import MenuAccessLogsManagement from './components/MenuAccessLogsManagement'; 
import ProductAddonsManagement from './components/ProductAddonsManagement'; 
import ProductsManagement from './components/ProductsManagement';
import ProductVariantsManagement from './components/ProductVariantsManagement';
import QRCodesManagement from './components/QRCodesManagement';
import RestaurantHoursManagement from './components/RestaurantHoursManagement';
import SpecialDaysManagement from './components/SpecialDaysManagement';
import TranslationsManagement from './components/TranslationsManagement';
import UsersManagement from './components/UsersManagement';
import PendingOrdersManagement from './components/PendingOrdersManagement';
import { Menu, X, ChevronDown, LogOut, User } from 'lucide-react';
import './App.css';

// Orders Panel Component - Sadece PendingOrdersManagement gösterir
const OrdersPanel = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PendingOrdersManagement />
    </div>
  );
};

// Main App Content with Navigation
const AppContent = () => {
  const [currentPage, setCurrentPage] = useState('restaurants');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, restaurants, hasPermission } = useAuth();

  const handleLogout = () => {
    logout();
    setCurrentPage('restaurants');
    setIsMobileMenuOpen(false);
  };

  const handlePageChange = (pageId) => {
    setCurrentPage(pageId);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownToggle = (dropdownName) => {
    console.log('Dropdown toggle:', dropdownName, 'Current open:', openDropdown);
    console.log('Products items:', groupedItems.products);
    console.log('QR items:', groupedItems.qr);
    console.log('Dropdown will be:', openDropdown === dropdownName ? 'closed' : 'opened');
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  // Filter navigation items based on user permissions
  const getNavigationItems = () => {
    const allItems = [
      {
        id: 'restaurants',
        label: 'Restoranlar',
        icon: '🏪',
        permission: 'manage_own_restaurant',
        category: 'main'
      },
      {
        id: 'categories',
        label: 'Kategoriler',
        icon: '📂',
        permission: 'manage_products',
        category: 'products'
      },
      {
        id: 'products',
        label: 'Ürünler',
        icon: '🍽️',
        permission: 'manage_products',
        category: 'products'
      },
      {
        id: 'variants',
        label: 'Ürün Varyantları',
        icon: '🔄',
        permission: 'manage_products',
        category: 'products'
      },
      {
        id: 'addons',
        label: 'Ürün Eklentileri',
        icon: '➕',
        permission: 'manage_products',
        category: 'products'
      },
      {
        id: 'qrmanagement',
        label: 'QR Yönetimi',
        icon: '🎯',
        permission: 'manage_own_restaurant',
        category: 'qr'
      }
    ];

    // Add Users Management only for SuperAdmin and Admin
    if (hasPermission('manage_users')) {
      allItems.push({
        id: 'users',
        label: 'Kullanıcılar',
        icon: '👥',
        permission: 'manage_users',
        category: 'admin'
      });
    }

    return allItems.filter(item => hasPermission(item.permission));
  };

  // Group navigation items by category
  const getGroupedNavigationItems = () => {
    const items = getNavigationItems();
    const grouped = {
      main: items.filter(item => item.category === 'main'),
      products: items.filter(item => item.category === 'products'),
      qr: items.filter(item => item.category === 'qr'),
      reports: items.filter(item => item.category === 'reports'),
      admin: items.filter(item => item.category === 'admin')
    };

    return grouped;
  };

  const navigationItems = getNavigationItems();
  const groupedItems = getGroupedNavigationItems();
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="App">
      {/* Header with User Info */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">Digital Menu System</h1>
            </div>
            
            {/* Desktop User Info and Logout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                </div>
                <div className="text-sm">
                  <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                  <div className="text-gray-500">{user.role}</div>
                </div>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Çıkış Yap
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                </div>
                <div className="text-sm">
                  <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                  <div className="text-gray-500">{user.role}</div>
                </div>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Çıkış Yap
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Navigation Menu */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40" style={{overflow: 'visible'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-4 overflow-visible" ref={dropdownRef}>
            {/* Main Items */}
            {groupedItems.main.map((item) => (
              <button
                key={item.id}
                onClick={() => handlePageChange(item.id)}
                className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 rounded-t-lg ${
                  currentPage === item.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}

            {/* Products Dropdown */}
            {groupedItems.products.length > 0 && (
              <div className="relative" style={{position: 'relative', overflow: 'visible'}}>
                <button
                  onClick={() => handleDropdownToggle('products')}
                  className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 rounded-t-lg flex items-center gap-2 ${
                    groupedItems.products.some(item => currentPage === item.id)
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span>🍽️</span>
                  Ürünler
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === 'products' && (
                  <div className="dropdown-menu" style={{zIndex: 99999}}>
                    {console.log('Rendering products dropdown with', groupedItems.products.length, 'items')}
                    {groupedItems.products.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handlePageChange(item.id)}
                        className={`dropdown-item ${currentPage === item.id ? 'active' : ''}`}
                      >
                        <span className="mr-2">{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* QR Dropdown */}
            {groupedItems.qr.length > 0 && (
              <div className="relative" style={{position: 'relative', overflow: 'visible'}}>
                <button
                  onClick={() => handleDropdownToggle('qr')}
                  className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 rounded-t-lg flex items-center gap-2 ${
                    groupedItems.qr.some(item => currentPage === item.id)
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span>📱</span>
                  QR
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'qr' ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === 'qr' && (
                  <div className="dropdown-menu" style={{zIndex: 99999}}>
                    {console.log('Rendering QR dropdown with', groupedItems.qr.length, 'items')}
                    {groupedItems.qr.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handlePageChange(item.id)}
                        className={`dropdown-item ${currentPage === item.id ? 'active' : ''}`}
                      >
                        <span className="mr-2">{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reports */}
            {groupedItems.reports.map((item) => (
              <button
                key={item.id}
                onClick={() => handlePageChange(item.id)}
                className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 rounded-t-lg ${
                  currentPage === item.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}

            {/* Admin */}
            {groupedItems.admin.map((item) => (
              <button
                key={item.id}
                onClick={() => handlePageChange(item.id)}
                className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 rounded-t-lg ${
                  currentPage === item.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-gray-700">
                {navigationItems.find(item => item.id === currentPage)?.label || 'Menü'}
              </span>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <span>Menüler</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {isMobileMenuOpen && (
              <div className="border-t border-gray-200 pt-3 pb-4">
                <div className="grid grid-cols-2 gap-2">
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handlePageChange(item.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentPage === item.id
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="text-base">{item.icon}</span>
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {currentPage === 'restaurants' && <RestaurantManagement />}
        {currentPage === 'categories' && <CategoriesManagement />}
        {currentPage === 'products' && <ProductsManagement />}
        {currentPage === 'variants' && <ProductVariantsManagement />}
        {currentPage === 'addons' && <ProductAddonsManagement />}
        {currentPage === 'menulogs' && <MenuAccessLogsManagement />}
        {currentPage === 'qrmanagement' && <QRCodesManagement />}
        {currentPage === 'hours' && <RestaurantHoursManagement />}
        {currentPage === 'specialdays' && <SpecialDaysManagement />}
        {currentPage === 'translations' && <TranslationsManagement />}
        {currentPage === 'users' && <UsersManagement />}
        {currentPage === 'pendingorders' && <PendingOrdersManagement />}
      </div>
    </div>
  );
};

// Main App Component with Router and Auth Provider
function App() {
  return (
    <Router basename="/ordermenu">
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/menu/:qrMenuId" element={<CustomerMenuInterface />} />
          <Route path="/orders" element={
            <ProtectedRoute>
              <OrdersPanel />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <AppContent />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;